import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Plus, Trash2 } from 'lucide-react'
import {
  Modal,
  Button,
  DateField,
  Input,
  Label,
  MultiSelect,
  cn,
  type SelectOption,
} from '@atrvd/saas-ui'
import { businessDate, isCalendarDate } from '@dcp/shared'
import { formatCurrency } from '@dcp/shared/utils/formatters'
import { AmountInput } from '@/components/banking/AmountInput'
import type { LocLine, LocReference } from '@/types/loc-availability'

/**
 * Props for {@link UpdateLocAvailabilityDialog}.
 *
 * @property open       - Controls dialog visibility.
 * @property reference  - The reference being edited; `null` while none selected.
 * @property onClose    - Called when the dialog should close (Discard / backdrop).
 * @property onSave      - Called with the edited reference when Update succeeds; the
 *                          returned promise resolves once the persistence mutation
 *                          settles so the dialog can await it before closing.
 * @property isSaving    - True while the caller's mutation is in flight; disables
 *                          the Update button to prevent a double-submit.
 */
interface UpdateLocAvailabilityDialogProps {
  open: boolean
  reference: LocReference | null
  onClose: () => void
  onSave: (updated: LocReference) => Promise<void>
  isSaving?: boolean
}

/**
 * Editable, form-local mirror of {@link LocLine}.
 *
 * Amounts are `number | null` (null = empty field) rather than `number` so the
 * AmountInput can represent a cleared field without coercing to 0. Everything
 * else matches LocLine so mapping back on save is a straight copy.
 */
interface EditableLocLine {
  id: string
  name: string
  facilities: string[]
  borrowingBaseAmount: number | null
  /** Preserved as-is on save — usage is not edited here (derived from bank txns). */
  locUsage: number
  effectiveDate: string
  note: string
  startingLocUsageDate: string
  startingLocUsageAmount: number | null
  /**
   * The starting-usage amount as it arrived from the server, kept as an
   * immutable baseline for the over-advance advisory (DCP-307 AC3).
   *
   * `locUsage` is delivered by the server as "starting amount + transaction
   * sum", so re-basing it needs the ORIGINAL starting amount, not the one the
   * user is currently typing. It is therefore never passed to `updateLine` —
   * treat it as read-only for the whole lifetime of the line.
   */
  seedStartingLocUsageAmount: number | null
}

/**
 * The line fields `updateLine` is allowed to patch.
 *
 * The three exclusions are not stylistic: `id` identifies the line (and keys the
 * touched-line map), `locUsage` is server-derived and only passed back through on
 * save, and `seedStartingLocUsageAmount` is the immutable baseline the
 * over-advance advisory re-bases against — rewriting it would make the advisory
 * drift as the user types. Encoding that in the type means a new control wired
 * through the funnel cannot break the invariant without a compile error.
 */
type EditableLocLineField = Exclude<
  keyof EditableLocLine,
  'id' | 'locUsage' | 'seedStartingLocUsageAmount'
>

/** Per-line validation errors (AC.7). A field is present only when invalid. */
interface LineErrors {
  /** Line name is editable on both standalone and pack lines and must not be blank
   * — an empty name would otherwise surface as a broken "Facility (in )" label in
   * another line's Facilities MultiSelect (see `optionsForLine`). It must also be
   * unique within the current lines — `optionsForLine` labels a taken facility as
   * "Facility (in <name>)", so two lines sharing a name make that label ambiguous. */
  name?: string
  borrowingBaseAmount?: string
  effectiveDate?: string
  /** Pack lines require ≥1 facility (DCP-167 AC.4); unused for standalone facilities. */
  facilities?: string
  /** Starting usage is optional (DCP-163) but, when typed, must be a number ≥ 0 (DCP-307 AC1). */
  startingLocUsageAmount?: string
  /** Starting usage is "as of" a past date — a future one is rejected, as is a
   * malformed / out-of-range value such as a 5-digit year (DCP-307 AC2). */
  startingLocUsageDate?: string
}

/** Build a fresh, empty editable line named `Line of Credit ${number}`. */
function makeEmptyLine(number: number): EditableLocLine {
  return {
    id: crypto.randomUUID(),
    name: `Line of Credit ${number}`,
    facilities: [],
    borrowingBaseAmount: null,
    locUsage: 0,
    effectiveDate: '',
    note: '',
    startingLocUsageDate: '',
    startingLocUsageAmount: null,
    // A brand-new line has no server baseline — nothing is folded into
    // `locUsage` (which is 0 here) yet.
    //
    // Known limitation of the over-advance advisory (DCP-307 AC3): `locUsage` is
    // a server aggregate that reports 0 when no `loc_lines` row exists, so a
    // facility that ALREADY has LINE OF CREDIT transactions still starts from a
    // 0 baseline in this form. The first-ever save of such a line can therefore
    // land in an over-advance the user was never warned about; it only appears on
    // the next refetch. This is inherent to deriving the advisory from an
    // aggregate the client cannot recompute locally, not something this dialog
    // can fix on its own.
    seedStartingLocUsageAmount: null,
  }
}

/**
 * Derive the next default line number from the MAX existing
 * "Line of Credit N" sequence number among `lines`, rather than
 * `lines.length` — so deleting a line and then adding a new one never
 * reuses a name that's still in use (e.g. delete "Line of Credit 2" out of
 * [1, 2, 3], then Add would otherwise produce a second "Line of Credit 3").
 * Lines whose name doesn't match the default pattern (renamed by the user)
 * are simply ignored rather than breaking the count.
 */
function nextLineNumber(lines: EditableLocLine[]): number {
  const pattern = /^Line of Credit (\d+)$/
  const max = lines.reduce((highest, line) => {
    const match = pattern.exec(line.name)
    const n = match ? Number(match[1]) : 0
    return Number.isFinite(n) ? Math.max(highest, n) : highest
  }, 0)
  return max + 1
}

/** Convert a domain line into its editable form-local shape. */
function toEditable(line: LocLine): EditableLocLine {
  return {
    id: line.id,
    name: line.name,
    facilities: [...line.facilities],
    borrowingBaseAmount: line.borrowingBaseAmount,
    locUsage: line.locUsage,
    effectiveDate: line.effectiveDate,
    note: line.note,
    startingLocUsageDate: line.startingLocUsageDate,
    startingLocUsageAmount: line.startingLocUsageAmount,
    // Baseline for the over-advance advisory: the amount already folded into
    // the server-computed `locUsage`.
    seedStartingLocUsageAmount: line.startingLocUsageAmount,
  }
}

/**
 * "Update Line of Credit Availability" dialog (DCP-283 / DCP-167).
 *
 * Layout mirrors the prototype: one bordered card per line whose header holds an
 * editable line-name pill (left) and a Remove control (right); then a
 * Reference / Facilities row, an Amount / Date / Note row (Note wider), and a
 * Starting-usage row. A dashed "Add Line of Credit" button and a
 * Total / Discard / Update footer complete it.
 *
 * Because the form is a dynamic list of lines with cross-line facility
 * exclusivity (a facility belongs to exactly one line) and per-line add/remove,
 * it is managed with plain `useState` rather than react-hook-form: the
 * cross-line constraint and the dynamic array are far clearer as derived local
 * state, and the data is in-memory only (no server mutation to coordinate).
 *
 * Facility handling differs by reference type:
 *  - `pack`     — each line has a Facilities MultiSelect scoped to the pack's
 *                 facilities; Add / Remove line controls are shown.
 *  - `facility` — exactly one line, no MultiSelect (the reference IS the single
 *                 facility) and no Add / Remove controls.
 */
export function UpdateLocAvailabilityDialog({
  open,
  reference,
  onClose,
  onSave,
  isSaving = false,
}: UpdateLocAvailabilityDialogProps) {
  /** Form-local editable copy of the reference's lines. */
  const [lines, setLines] = useState<EditableLocLine[]>([])

  /**
   * Whether an Update has been attempted. One of the two halves of the error
   * gate (the other is `touchedLines`); it exists so a freshly opened dialog
   * isn't littered with red fields. On its own it can never surface anything,
   * because the Update button is disabled while the form is invalid — see the
   * gate at the line render for the full reasoning.
   */
  const [submitted, setSubmitted] = useState(false)

  /**
   * Ids of the lines the user has edited since the dialog was seeded; errors are
   * surfaced for a line as soon as it is in here (see the render gate below).
   *
   * Granularity is per-LINE, not per-field, on purpose: it also reveals a
   * PRE-EXISTING invalid value on a line the user is editing for an unrelated
   * reason. The concrete case is a legacy row whose Starting LOC Usage Date is
   * in the future (no DB CHECK guards that column, and the previous UI used an
   * unbounded DatePicker) — the user only wants to change the Note, Update stays
   * disabled, and per-field granularity would never tell them which field is at
   * fault.
   *
   * A freshly opened dialog still shows nothing, and neither does a line just
   * created by "Add Line of Credit": a newly seeded empty line is legitimately
   * invalid at that moment (no amount, no effective date, no facilities) and
   * must not open red.
   *
   * Entries for removed lines are deliberately left behind — line ids are unique
   * (`crypto.randomUUID`), so a stale entry can never match a later line, and
   * the whole record is dropped when the dialog reseeds.
   */
  const [touchedLines, setTouchedLines] = useState<Record<string, boolean>>({})

  const isPack = reference?.type === 'pack'

  /**
   * Seed the form when the dialog opens or a different reference is passed.
   * A deep-ish copy is taken so in-progress edits never mutate the page's
   * source data until Update is pressed.
   */
  useEffect(() => {
    if (!open || !reference) return
    // A freshly-editable pack can arrive with zero lines (the hook does NOT
    // synthesize one for packs). Seed a single line PRE-SELECTED with all of the
    // pack's active facilities (DCP-167 AC.1: "all active facilities of the given
    // pack pre-selected by default") so a brand-new pack is valid to save without
    // manual selection. (makeEmptyLine — used by "Add Line of Credit" — stays
    // empty, since added lines pick from the not-yet-assigned facilities.)
    const seeded =
      reference.type === 'pack' && reference.lines.length === 0
        ? [{ ...makeEmptyLine(1), facilities: [...reference.facilities] }]
        : reference.lines.map(toEditable)
    setLines(seeded)
    setSubmitted(false)
    // Reseeding replaces every line, so the touched set must go with it —
    // otherwise a different reference could open with fields already red.
    setTouchedLines({})
  }, [open, reference])

  /** Live total borrowing base across all lines (null amounts count as 0). */
  const totalBorrowingBase = useMemo(
    () => lines.reduce((sum, line) => sum + (line.borrowingBaseAmount ?? 0), 0),
    [lines],
  )

  /**
   * facility name → index of the line that already owns it. Used to disable a
   * facility in every OTHER line's MultiSelect so a facility can never be
   * assigned to two lines (cross-line exclusivity).
   */
  const facilityOwner = useMemo(() => {
    const owner = new Map<string, number>()
    lines.forEach((line, index) => {
      for (const facility of line.facilities) {
        if (!owner.has(facility)) owner.set(facility, index)
      }
    })
    return owner
  }, [lines])

  /**
   * Per-line validation (AC.7): line name must not be blank (an empty name would
   * otherwise render as a broken "Facility (in )" label elsewhere) and must be
   * unique among the current lines (a duplicate name makes that same label
   * ambiguous — "Facility (in <name>)" could refer to either line); borrowing
   * base must be a finite number >= 0 and mandatory (DCP-348: zero is a valid
   * borrowing base); effective date is mandatory
   * and must be a well-formed date (but may be in the future). DCP-307 adds:
   * starting LOC usage amount, when present, must be a number ≥ 0, and the
   * starting LOC usage date, when present, must be a well-formed date that is
   * not in the future. Returns an array parallel to `lines`.
   */
  const lineErrors = useMemo<LineErrors[]>(
    () =>
      lines.map((line, index) => {
        const errors: LineErrors = {}
        // Applies to both standalone and pack lines — the name Input is editable
        // in either case (see the header Input below).
        const trimmedName = line.name.trim()
        if (!trimmedName) {
          errors.name = 'Line name is required'
        } else if (
          // Only relevant when there is more than one line (packs) — a single
          // standalone line can never collide with itself.
          lines.some(
            (other, otherIndex) =>
              otherIndex !== index && other.name.trim().toLowerCase() === trimmedName.toLowerCase(),
          )
        ) {
          errors.name = 'Line name must be unique within this pack'
        }
        // DCP-348 — the amount is MANDATORY (blank and NaN stay errors) but 0
        // is a legitimate value: a line whose collateral currently supports
        // nothing still exists and still has to be reported. Negatives remain
        // rejected. This mirrors the DB exactly, which is the point — the
        // `loc_lines_borrowing_base_nonneg CHECK (borrowing_base_amount IS NULL
        // OR borrowing_base_amount >= 0)` constraint was widened in the same
        // change (20260824000000_dcp348_borrowing_base_allow_zero.sql), so a UI
        // that still refused 0 would just be stricter than the data model.
        const amount = line.borrowingBaseAmount
        if (amount == null || !Number.isFinite(amount) || amount < 0) {
          errors.borrowingBaseAmount = 'Enter an amount of 0 or greater'
        }
        // Presence, then shape + parseability, with two DIFFERENT messages on
        // purpose: a blank field is a "required" problem, while a filled-in but
        // unusable value is a "not a valid date" problem. The latter reuses the
        // same wording as the starting-usage date so both date fields report a
        // malformed value identically.
        //
        // `isCalendarDate` (@dcp/shared) is the product's one answer to "is
        // this a real calendar day?": it gates the SHAPE (bare `YYYY-MM-DD`,
        // which is what rejects the 5-/6-digit year a native date input can
        // emit — typing `01/01/10000` yields `'10000-01-01'`) and then parses
        // STRICTLY, which is what rejects a well-shaped day that does not exist
        // (`'2024-02-30'`, `'2023-02-29'`). A lenient `dayjs(v).isValid()` rolls
        // those over and reports them as valid.
        //
        // There is deliberately NO upper bound here — the BA struck "Date
        // Effective" out of AC2, so a future effective date is legitimate.
        if (!line.effectiveDate) {
          errors.effectiveDate = 'Effective date is required'
        } else if (!isCalendarDate(line.effectiveDate)) {
          errors.effectiveDate = 'Enter a valid date'
        }
        // DCP-307 AC1 — starting LOC usage amount. The field is OPTIONAL
        // (DCP-163), so null/empty is valid, and 0 is valid too. The DB
        // constraint for this column is `loc_lines_starting_usage_nonneg CHECK
        // (starting_loc_usage_amount IS NULL OR starting_loc_usage_amount >= 0)`
        // — the UI mirrors exactly that, no stricter. Since DCP-348 the
        // borrowing base follows the same non-negative rule; the two differ now
        // only in that the base is MANDATORY while this baseline is optional.
        const starting = line.startingLocUsageAmount
        if (starting != null && Number.isNaN(starting)) {
          errors.startingLocUsageAmount = 'Enter a valid amount'
        } else if (starting != null && starting < 0) {
          errors.startingLocUsageAmount = 'Enter an amount of 0 or greater'
        }
        // DCP-307 AC2 — the starting usage is recorded "as of" a date that has
        // already happened. An empty date stays valid (the field is optional).
        //
        // `isCalendarDate` runs FIRST and the future check only afterwards:
        // comparing the two strings with `>` is a chronological comparison ONLY
        // because both sides are then fixed-width `YYYY-MM-DD` with a 4-digit
        // year — the shape tier of that helper is what guarantees it. ISO strings
        // of DIFFERENT widths sort by character, not by date, which is exactly
        // how `'10000-01-01'` used to slip through. The right-hand side is
        // `businessDate()`, not the viewer's day: the value lands in a `DATE`
        // column, so "today" must be the company's calendar day (America/Chicago)
        // for writers and readers of that column to agree.
        //
        // Known consequence of this being a UI-only rule: no DB CHECK guards
        // `starting_loc_usage_date` and the previous UI's DatePicker was
        // unbounded, so rows with a future starting date can already exist. Such
        // a line is invalid here, so it cannot be saved again — not even for an
        // unrelated edit like the Note — until the date is corrected. That is
        // accepted (AC2 is an explicit blocking rule, not an advisory); the
        // touched-line error gate is what makes the reason visible.
        if (line.startingLocUsageDate) {
          if (!isCalendarDate(line.startingLocUsageDate)) {
            errors.startingLocUsageDate = 'Enter a valid date'
          } else if (line.startingLocUsageDate > businessDate()) {
            errors.startingLocUsageDate = 'Date cannot be in the future'
          }
        }
        // DCP-167 AC.4: every pack line must cover at least one facility.
        // Standalone facilities have no MultiSelect, so this never applies.
        if (isPack && line.facilities.length === 0) {
          errors.facilities = 'Select at least one facility'
        }
        return errors
      }),
    [lines, isPack],
  )

  /** True when every line passes validation — gates the Update button. */
  const isValid = useMemo(
    () => lineErrors.every((errors) => Object.keys(errors).length === 0),
    [lineErrors],
  )

  /**
   * Patch a single field on the line at `index` and mark that line as touched so
   * its validation messages become visible.
   *
   * This is the single funnel for EVERY editable control in a line — the name
   * Input, the Facilities MultiSelect, Borrowing Base Amount, Date Effective,
   * Note and both starting-usage fields all call it — so marking touched here is
   * sufficient; no control writes to `lines` on its own. Keep it that way: a
   * control that bypasses this helper would also bypass the error gate.
   *
   * The key type is narrowed to {@link EditableLocLineField}, so the fields the
   * form must never rewrite — `id`, the server-derived `locUsage` and the
   * advisory's `seedStartingLocUsageAmount` baseline — are rejected by the
   * compiler rather than by convention.
   */
  const updateLine = <K extends EditableLocLineField>(
    index: number,
    key: K,
    value: EditableLocLine[K],
  ) => {
    // The id is read from the render-time `lines`, not from the setState
    // updater: `index` originates in the same render's `lines.map(...)`, so the
    // two cannot disagree, and marking touched must not depend on the queued
    // line update.
    const touchedId = lines[index]?.id
    if (touchedId) {
      setTouchedLines((prev) => (prev[touchedId] ? prev : { ...prev, [touchedId]: true }))
    }
    setLines((prev) =>
      prev.map((line, i) => (i === index ? { ...line, [key]: value } : line)),
    )
  }

  /** Append a blank line (pack only), numbered past the highest existing default name. */
  const handleAddLine = () => {
    setLines((prev) => [...prev, makeEmptyLine(nextLineNumber(prev))])
  }

  /** Remove the line at `index` (guarded to keep at least one line). */
  const handleRemoveLine = (index: number) => {
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev))
  }

  /**
   * Facility MultiSelect options for a given line: every pack facility, with
   * facilities owned by ANOTHER line disabled and annotated with that line's
   * name so the exclusivity is visible, not just enforced.
   */
  const optionsForLine = (index: number): SelectOption[] => {
    if (!reference) return []
    return reference.facilities.map((facility) => {
      const ownerIndex = facilityOwner.get(facility)
      const takenByOther = ownerIndex !== undefined && ownerIndex !== index
      return {
        value: facility,
        label: takenByOther ? `${facility} (in ${lines[ownerIndex].name})` : facility,
        disabled: takenByOther,
      }
    })
  }

  /**
   * Validate and, when valid, map the edited lines back and hand off to
   * `onSave`. `onSave` runs the actual persistence mutation and resolves once
   * it settles; the await here just lets this handler wait alongside it —
   * any failure is already surfaced to the user via the mutation's own
   * `onError` toast (see the page's `handleSave`), so it is intentionally
   * swallowed here rather than rethrown to avoid an unhandled rejection.
   */
  const handleUpdate = async () => {
    setSubmitted(true)
    if (!reference || !isValid) return

    const savedLines: LocLine[] = lines.map((line) => ({
      id: line.id,
      name: line.name,
      // Standalone facilities keep the reference's single facility; packs use
      // the per-line selection.
      facilities: isPack ? line.facilities : [...reference.facilities],
      borrowingBaseAmount: line.borrowingBaseAmount ?? 0,
      locUsage: line.locUsage,
      effectiveDate: line.effectiveDate,
      note: line.note,
      startingLocUsageDate: line.startingLocUsageDate,
      // Defensive: unparseable text is kept as NaN in form state (see the
      // field's onChange) and `isValid` already blocks Update in that case, so
      // this is unreachable today — but NaN is not representable in JSON and
      // would silently serialise to null in the RPC payload, so collapse it
      // explicitly rather than relying on that.
      startingLocUsageAmount: Number.isNaN(line.startingLocUsageAmount)
        ? null
        : line.startingLocUsageAmount,
    }))

    // Recompute the reference's facility superset from its lines (packs only);
    // standalone facilities keep their single facility.
    const facilities = isPack
      ? [...new Set(savedLines.flatMap((line) => line.facilities))]
      : [...reference.facilities]

    try {
      await onSave({ ...reference, facilities, lines: savedLines })
    } catch {
      // Error already surfaced via the mutation's onError toast — nothing
      // further to do here; the dialog stays open so the user can retry.
    }
  }

  if (!reference) return null

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Update Line of Credit Availability"
      size="xl"
      footer={
        // Total (left) + Discard / Update (right) — mirrors the prototype footer.
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm text-muted-foreground">
            Total borrowing base:{' '}
            <b className="text-base font-bold text-foreground">
              {formatCurrency(totalBorrowingBase)}
            </b>
          </span>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
              Discard
            </Button>
            {/* Primary (blue) — matches the prototype's #2563eb Update button.
                Disabled while isValid fails OR a save is already in flight
                (prevents a double-submit while the mutation resolves). */}
            <Button type="button" onClick={handleUpdate} disabled={!isValid || isSaving}>
              Update
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3.5 px-1">
        {/* One bordered card per line (prototype: border #e8eaed, radius 12, p-18).
            No own max-height / overflow here (DCP-312): the saas-ui Modal body is
            already a scroll container (max-h-[60vh] overflow-y-auto), so a nested
            one produced a second, inner scrollbar. Letting the list flow makes the
            modal body the single scroller. */}
        <div className="space-y-3.5">
          {lines.map((line, index) => {
            /*
             * Show this line's errors once an Update has been attempted OR once
             * the user has edited the line.
             *
             * The touched half is not a nicety — without it the messages are
             * unreachable. `submitted` flips only inside `handleUpdate`, the
             * Update button carries `disabled={!isValid || isSaving}`, and a
             * disabled native <button> never fires onClick; so `submitted` could
             * only ever become true at an instant when `isValid` was already
             * true (nothing to report), and a successful save closes the dialog.
             * The Update button's gate is intentionally unchanged (documented
             * DCP-167 behaviour) — this makes the REASON visible, not the gate
             * looser.
             */
            const errors = submitted || touchedLines[line.id] ? lineErrors[index] : {}
            /**
             * Usage this line WOULD report once the pending starting-AMOUNT edit
             * is saved: `locUsage` arrives from the server as "starting amount +
             * transaction sum", so the baseline already baked in is swapped out
             * and the edited value swapped in (`locUsage - seed + typed`, i.e.
             * "transaction sum + typed"). Comparing raw `locUsage` would judge a
             * stale state while the user types.
             *
             * What this re-base does NOT cover: the transaction sum itself
             * depends on two other fields this same dialog edits, and neither is
             * modelled here —
             *  - the Starting LOC Usage Date is the server's floor for which
             *    transactions count, so clearing it means MORE history will be
             *    counted after save than is reflected here; a genuine
             *    over-advance can stay hidden until then;
             *  - moving a facility between pack lines moves its transactions
             *    with it, so an advisory can remain on the line that no longer
             *    owns them.
             * Both self-correct on the refetch that follows a successful save.
             * No suppression is applied while those fields are dirty, on
             * purpose: hiding the advisory would hide true positives too.
             */
            const effectiveUsage =
              line.locUsage -
              (line.seedStartingLocUsageAmount ?? 0) +
              (line.startingLocUsageAmount ?? 0)
            const borrowingBase = line.borrowingBaseAmount
            /**
             * DCP-307 AC3 advisory gate. `Number.isFinite` on both sides keeps
             * a NaN (mid-typing garbage in either amount field) from rendering
             * a "$NaN" banner. Deliberately independent of both `submitted` and
             * `touchedLines` — the advisory is live information about the data,
             * not a validation message about the user's input.
             *
             * DCP-348 relaxed the gate from `> 0` to `>= 0`: now that a zero
             * base is a legitimate value, usage against it is the MOST
             * over-advanced a line can be, and staying silent there would show
             * "base $0.00 / usage $5,000.00" as if it were fine. A NULL base
             * (nothing entered yet) still shows nothing to advise against.
             */
            const showOverAdvance =
              borrowingBase != null &&
              Number.isFinite(borrowingBase) &&
              borrowingBase >= 0 &&
              Number.isFinite(effectiveUsage) &&
              effectiveUsage > borrowingBase
            return (
              <div key={line.id} className="rounded-xl border border-border p-[18px]">
                {/* Header: editable line-name pill (left) + Remove line (right). */}
                <div className="mb-3.5 flex items-start justify-between gap-3">
                  <div className="max-w-[280px] flex-1 space-y-1.5">
                    <Input
                      aria-label={`Line name for line ${index + 1}`}
                      value={line.name}
                      onChange={(e) => updateLine(index, 'name', e.target.value)}
                      placeholder="Line of Credit"
                      title="Rename this borrowing base"
                      // DCP-307 AC5: mirrors the DB constraint
                      // `loc_lines_name_not_blank` (char_length(name) <= 255).
                      maxLength={255}
                      aria-invalid={errors.name ? 'true' : 'false'}
                      className={cn(
                        'bg-muted text-sm font-bold',
                        errors.name && 'border-destructive',
                      )}
                    />
                    {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
                  </div>
                  {/* Remove line — always shown (matches the prototype), but
                      disabled while only one line remains. For a standalone
                      facility there is always exactly one line, so it stays
                      disabled; multi-line packs (DCP-167) enable it. */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveLine(index)}
                    disabled={lines.length <= 1}
                    className="h-8 shrink-0 text-destructive hover:text-destructive"
                    aria-label={`Remove ${line.name}`}
                  >
                    <Trash2 className="mr-1 h-4 w-4" />
                    Remove line
                  </Button>
                </div>

                {/* Reference (read-only) + Facilities (packs only), side by side. */}
                <div className={cn('grid gap-3.5', isPack ? 'md:grid-cols-2' : 'grid-cols-1')}>
                  <div className="space-y-1.5">
                    <Label>Reference</Label>
                    <Input
                      value={reference.name}
                      readOnly
                      disabled
                      className="bg-muted text-muted-foreground"
                    />
                  </div>

                  {isPack && (
                    <div className="space-y-1.5">
                      <Label>
                        Facilities <span className="text-destructive">*</span>
                      </Label>
                      <MultiSelect
                        options={optionsForLine(index)}
                        value={line.facilities}
                        onChange={(value) => updateLine(index, 'facilities', value)}
                        placeholder="Select facilities…"
                        searchable
                        // Built-in error slot renders the message below the control.
                        error={errors.facilities}
                      />
                    </div>
                  )}
                </div>

                {/* Amount | Date Effective | Note (Note wider, like the prototype). */}
                <div className="mt-3.5 grid gap-3.5 md:grid-cols-[1fr_1fr_1.4fr]">
                  <div className="space-y-1.5">
                    <Label htmlFor={`loc-line-amount-${line.id}`}>
                      Borrowing Base Amount <span className="text-destructive">*</span>
                    </Label>
                    <AmountInput
                      id={`loc-line-amount-${line.id}`}
                      value={line.borrowingBaseAmount ?? undefined}
                      onChange={(value) =>
                        updateLine(
                          index,
                          'borrowingBaseAmount',
                          value === undefined || Number.isNaN(value) ? null : value,
                        )
                      }
                      placeholder="0.00"
                      aria-invalid={errors.borrowingBaseAmount ? 'true' : 'false'}
                      className={errors.borrowingBaseAmount ? 'border-destructive' : undefined}
                    />
                    {errors.borrowingBaseAmount && (
                      <p className="text-sm text-destructive">{errors.borrowingBaseAmount}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor={`loc-line-date-${line.id}`}>
                      Date Effective <span className="text-destructive">*</span>
                    </Label>
                    {/* DCP-307 AC2.1: typable date. Intentionally NO `max` here
                        — the BA struck "Date Effective" out of AC2, so a future
                        effective date is legitimate (a borrowing base can be
                        agreed to take effect later). Do not "fix" this by
                        adding a bound. */}
                    <DateField
                      id={`loc-line-date-${line.id}`}
                      value={line.effectiveDate}
                      onValueChange={(iso) => updateLine(index, 'effectiveDate', iso)}
                      aria-invalid={errors.effectiveDate ? 'true' : 'false'}
                      className={cn(errors.effectiveDate && 'border-destructive')}
                    />
                    {errors.effectiveDate && (
                      <p className="text-sm text-destructive">{errors.effectiveDate}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor={`loc-line-note-${line.id}`}>Note</Label>
                    <Input
                      id={`loc-line-note-${line.id}`}
                      value={line.note}
                      onChange={(e) => updateLine(index, 'note', e.target.value)}
                      placeholder="Type here"
                      // DCP-307 AC6: bound new input only. `loc_lines.note` is
                      // an unbounded TEXT column, so a BLOCKING length rule
                      // would make a pre-existing longer note unsavable —
                      // `maxLength` caps typing/pasting without breaking legacy
                      // rows, which is why there is deliberately no matching
                      // entry in `lineErrors`.
                      maxLength={500}
                    />
                  </div>
                </div>

                {/* Starting LOC usage date | amount. */}
                <div className="mt-3.5 grid gap-3.5 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor={`loc-line-start-date-${line.id}`}>
                      Starting LOC Usage Date
                    </Label>
                    {/* `max` bounds the browser picker to today (DCP-307 AC2),
                        but it is only an affordance: a typed future date — and
                        even a 5-digit year, which `max` cannot express — still
                        reaches state, so `lineErrors` re-checks both the shape
                        and the bound. */}
                    <DateField
                      id={`loc-line-start-date-${line.id}`}
                      value={line.startingLocUsageDate}
                      onValueChange={(iso) => updateLine(index, 'startingLocUsageDate', iso)}
                      max={businessDate()}
                      aria-invalid={errors.startingLocUsageDate ? 'true' : 'false'}
                      className={cn(errors.startingLocUsageDate && 'border-destructive')}
                    />
                    {errors.startingLocUsageDate && (
                      <p className="text-sm text-destructive">{errors.startingLocUsageDate}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor={`loc-line-start-amount-${line.id}`}>
                      Starting LOC Usage Amount
                    </Label>
                    <AmountInput
                      id={`loc-line-start-amount-${line.id}`}
                      value={line.startingLocUsageAmount ?? undefined}
                      onChange={(value) =>
                        // Only `undefined` (a genuinely empty field) collapses to
                        // null. NaN is kept in state ON PURPOSE (DCP-307 AC1):
                        // mapping it to null too made unparseable text such as
                        // "abc" indistinguishable from "not filled in", so the
                        // typed garbage was silently discarded on save instead
                        // of being reported.
                        updateLine(
                          index,
                          'startingLocUsageAmount',
                          value === undefined ? null : value,
                        )
                      }
                      placeholder="0.00"
                      aria-invalid={errors.startingLocUsageAmount ? 'true' : 'false'}
                      className={errors.startingLocUsageAmount ? 'border-destructive' : undefined}
                    />
                    {errors.startingLocUsageAmount && (
                      <p className="text-sm text-destructive">{errors.startingLocUsageAmount}</p>
                    )}
                  </div>
                </div>

                {/* DCP-307 AC3 — over-advance advisory. NON-blocking: it never
                    feeds `isValid` (drawing beyond the borrowing base is a real
                    business state that must still be recordable) and it is not
                    gated on `submitted`, so the user sees it while typing.
                    role="status" (polite) rather than role="alert" for the same
                    reason — but treat the advisory as a VISUAL affordance:
                    because this region is mounted together with its content
                    rather than pre-existing as an empty live region, screen
                    readers generally will NOT announce it. Fixing that properly
                    needs an always-mounted container, which would add a
                    permanent `space-y` gap to every line, so it is left as is
                    deliberately.
                    Amber classes copied verbatim from ViewBankTransactionsDialog's
                    `AmberNotice` (the DCP-161 missing-data convention); that
                    helper is local and unexported, so this pattern is currently
                    copy-pasted across files. Extracting a shared component is
                    deliberately out of scope for this ticket. */}
                {showOverAdvance && (
                  <div
                    role="status"
                    className="mt-3.5 flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    {/* `borrowingBase` is `line.borrowingBaseAmount`, already
                        narrowed to a finite, non-negative number by
                        `showOverAdvance` — zero included since DCP-348. */}
                    <span>
                      Over-advance: LOC usage of {formatCurrency(effectiveUsage)} exceeds the
                      borrowing base of {formatCurrency(borrowingBase)}.
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Add line — packs only; dashed, full-width, blue like the prototype. */}
        {isPack && (
          <Button
            type="button"
            variant="outline"
            onClick={handleAddLine}
            className="w-full border-dashed border-primary/40 text-primary hover:bg-primary/5 hover:text-primary"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Line of Credit
          </Button>
        )}
      </div>
    </Modal>
  )
}
